import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { timecardService } from "@/services/timecardService";
import { TimecardType } from "@/types/db/time_cards";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, EditableOption } from "@/components/NewTable";
import { theme } from "@/constants/themes";

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral">> = {
  APPROVED: "complete",
  PENDING: "pending",
};

export interface RelatedTimecardsScreenProps {
  showApproved?: boolean;
}

export function RelatedTimecardsScreen({ showApproved = false }: RelatedTimecardsScreenProps) {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { time_cards: timecardPermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [timecards, setTimecards] = useState<TimecardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.time_cards?.plural || "Time Cards"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.time_cards?.plural]);

  useEffect(() => {
    const fetchTimecards = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedTimecards: TimecardType[] = [];

        switch (currentEntity.currentPanel) {
          case "user":
            fetchedTimecards = await timecardService.getTimecardsByUserId(currentEntity.currentId);
            break;
          case "project":
            fetchedTimecards = await timecardService.getTimecardsByProjectId(currentEntity.currentId);
            break;
          case "shift":
            fetchedTimecards = await timecardService.getTimecardsByShiftId(currentEntity.currentId);
            break;
          default:
            console.warn(`No timecard relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setTimecards(fetchedTimecards);
      } catch (err: any) {
        console.error("Failed to fetch related timecards:", err);
        setError(err.message || "Failed to load timecards");
      } finally {
        setLoading(false);
      }
    };

    fetchTimecards();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // Filter timecards based on showApproved toggle
  const filteredTimecards = useMemo(() => {
    // Sort by date descending
    const sorted = [...timecards].sort((a, b) => {
      const aTime = (a as any).time?.start_time ? new Date((a as any).time.start_time).getTime() : 0;
      const bTime = (b as any).time?.start_time ? new Date((b as any).time.start_time).getTime() : 0;
      return bTime - aTime;
    });

    if (!showApproved) {
      return sorted.filter((tc) => tc.status !== "APPROVED");
    }

    return sorted;
  }, [timecards, showApproved]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status || "PENDING"] || "pending";
    return colors[colorKey].border;
  };

  const formatHoursWorked = (hoursWorked?: number | null) => {
    if (hoursWorked == null) return "—";
    return `${hoursWorked.toFixed(1)}h`;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatUserName = (user?: { first_name?: string; last_name?: string; preferred_name?: string } | null) => {
    if (!user) return null;
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || null;
  };

  // Status options for editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "PENDING", label: "Pending", color: colors.pending.border },
      { value: "APPROVED", label: "Approved", color: colors.complete.border },
    ],
    [colors],
  );

  // Handle edit callback
  const handleEdit = useCallback(async (row: TimecardType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await timecardService.updateTimecard(row.id, { [key]: newValue });
      setTimecards((prev) => prev.map((tc) => (tc.id === row.id ? { ...tc, [key]: newValue } : tc)));
    } catch (err) {
      console.error("Failed to update timecard:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: TimecardType) => {
    if (!row.id) return;

    try {
      await timecardService.deleteTimecardById(row.id);
      setTimecards((prev) => prev.filter((tc) => tc.id !== row.id));
    } catch (err) {
      console.error("Failed to delete timecard:", err);
    }
  }, []);

  const handleShiftPress = useCallback(
    (timecard: TimecardType) => {
      if (!(timecard as any).shift?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("shift", (timecard as any).shift.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (timecard: TimecardType) => {
      if (!(timecard as any).project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", (timecard as any).project.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleClientPress = useCallback(
    (timecard: TimecardType) => {
      if (!(timecard as any).client?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("client", (timecard as any).client.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<TimecardType>[] = useMemo(() => {
    const cols: ColumnDef<TimecardType>[] = [];

    // User column (skip when viewing from user panel)
    if (currentEntity?.currentPanel !== "user") {
      cols.push({
        key: "user",
        header: displayNames?.users?.name || "User",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => formatUserName(row.user) || "—",
      });
    }

    // Shift column (skip when viewing from shift panel)
    if (currentEntity?.currentPanel !== "shift") {
      cols.push({
        key: "shift",
        header: displayNames?.shifts?.name || "Shift",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.shift?.name || "—",
        onPress: (row) => handleShiftPress(row),
      });
    }

    // Project column (skip when viewing from project panel)
    if (currentEntity?.currentPanel !== "project") {
      cols.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    // Client column (only show when viewing from user panel)
    if (currentEntity?.currentPanel === "user") {
      cols.push({
        key: "client",
        header: displayNames?.clients?.name || "Client",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.client?.name || "—",
        onPress: (row) => handleClientPress(row),
      });
    }

    // Position column
    cols.push({
      key: "position",
      header: "Position",
      width: 120,
      accessor: (row: any) => row.position?.name || "—",
    });

    // Status column with editable picker
    cols.push({
      key: "status",
      header: "Status",
      width: 120,
      align: "center",
      searchable: true,
      searchValue: (_, row) => row.status || "PENDING",
      editable: {
        type: "picker",
        options: statusOptions,
        placeholder: "Select status",
      },
      render: (value, row) => (
        <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
          <ThemedText style={styles.statusText}>{row.status || "PENDING"}</ThemedText>
        </View>
      ),
    });

    // Date column
    cols.push({
      key: "date",
      header: "Date",
      width: 120,
      searchable: false,
      accessor: (row: any) => row.time?.start_time,
      render: (value) => <ThemedText style={styles.dateText}>{value ? formatDate(value) : "—"}</ThemedText>,
    });

    // Hours column
    cols.push({
      key: "hours_worked",
      header: "Hours",
      width: 80,
      align: "right",
      searchable: false,
      accessor: (row: any) => row.hours_worked,
      render: (value) => <ThemedText style={styles.dateText}>{formatHoursWorked(value as number | null)}</ThemedText>,
    });

    return cols;
  }, [
    currentEntity?.currentPanel,
    statusOptions,
    formatDate,
    formatUserName,
    getStatusColor,
    handleShiftPress,
    handleProjectPress,
    handleClientPress,
    displayNames,
  ]);

  const timecardsName = displayNames?.time_cards?.plural?.toLowerCase() || "timecards";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {timecardsName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<TimecardType>
        data={filteredTimecards}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        onEdit={handleEdit}
        canEdit={timecardPermissions.update}
        onDelete={handleDelete}
        canDelete={timecardPermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "date", direction: "desc" }}
        emptyMessage={`No ${timecardsName} found`}
        emptyIcon="clock-outline"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  dateText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
